import React, { useEffect, useState, Component } from 'react';
import { IndividualExperience, ExperienceRecord, VerificationStatus, ExperienceType, ExitType } from '../types/experience';
import { Condition } from '../types/condition';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { XIcon, CheckCircleIcon, AlertTriangleIcon, ExternalLinkIcon, PencilIcon, SaveIcon, AlertCircleIcon, PlusIcon } from 'lucide-react';
interface RecordDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  record: IndividualExperience | null;
  guarantor: ExperienceRecord | null;
  onVerify: (id: string) => void;
  onFlag: (id: string) => void;
  onSkip: (id: string) => void;
  onUpdateNotes: (id: string, notes: string) => void;
  onUpdateRecord: (id: string, updates: Partial<IndividualExperience>) => void;
  conditions: Condition[];
  onCreateCondition: () => void;
}
export function RecordDrawer({
  isOpen,
  onClose,
  record,
  guarantor,
  onVerify,
  onFlag,
  onSkip,
  onUpdateNotes,
  onUpdateRecord,
  conditions,
  onCreateCondition
}: RecordDrawerProps) {
  const [notes, setNotes] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    if (record) {
      setNotes(record.triageNotes || '');
      setIsDirty(false);
      setIsEditing(false);
    }
  }, [record?.Id]);
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
    setIsDirty(true);
  };
  const handleSaveNotes = () => {
    if (record) {
      onUpdateNotes(record.Id, notes);
      setIsDirty(false);
    }
  };
  const handleInputChange = (field: keyof IndividualExperience, value: any) => {
    if (record) {
      onUpdateRecord(record.Id, {
        [field]: value
      });
    }
  };
  const handleAddressChange = (field: 'street' | 'city' | 'state' | 'zip', value: string) => {
    if (record) {
      onUpdateRecord(record.Id, {
        Property_Address__c: {
          ...record.Property_Address__c,
          [field]: value
        }
      });
    }
  };
  const handleToggleEdit = () => {
    if (isEditing) handleSaveNotes();
    setIsEditing(!isEditing);
  };
  if (!isOpen || !record || !guarantor) return null;
  const formatCurrency = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const toInputDate = (dateString: string | null) => {
    if (!dateString) return '';
    return dateString.split('T')[0];
  };
  // Fixed property links — Zillow deep-link, removed Forecasa, fixed Realtor
  const buildPropertyLinks = (address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  }) => {
    const encodedAddress = encodeURIComponent(`${address.street}, ${address.city}, ${address.state} ${address.zip}`);
    // Zillow: use their search endpoint which reliably finds the property
    const zillowUrl = `https://www.zillow.com/homes/${encodeURIComponent(`${address.street}, ${address.city}, ${address.state} ${address.zip}`)}_rb/`;
    // Realtor.com: use search endpoint for reliable deep-linking
    const realtorUrl = `https://www.realtor.com/realestateandhomes-search/${address.city.replace(/\s+/g, '-')}_${address.state}?keywords=${encodeURIComponent(address.street)}`;
    // Google Maps as a reliable fallback
    const googleUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    return [{
      name: 'Zillow',
      url: zillowUrl,
      color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
      iconColor: 'text-blue-500'
    }, {
      name: 'Realtor.com',
      url: realtorUrl,
      color: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
      iconColor: 'text-red-500'
    }, {
      name: 'Google Maps',
      url: googleUrl,
      color: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
      iconColor: 'text-green-500'
    }];
  };
  // Reusable toggle row for boolean flags
  const FlagRow = ({
    label,
    value,
    onChange,
    editing,
    activeColor = 'yellow'
  }: {
    label: string;
    value: boolean;
    onChange: (val: boolean) => void;
    editing: boolean;
    activeColor?: 'yellow' | 'green' | 'purple' | 'cyan' | 'orange';
  }) => {
    const colorMap = {
      yellow: {
        active: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
        badge: 'bg-yellow-100 text-yellow-800'
      },
      green: {
        active: 'bg-green-100 text-green-800 border border-green-200',
        badge: 'bg-green-100 text-green-800'
      },
      purple: {
        active: 'bg-purple-100 text-purple-800 border border-purple-200',
        badge: 'bg-purple-100 text-purple-800'
      },
      cyan: {
        active: 'bg-cyan-100 text-cyan-800 border border-cyan-200',
        badge: 'bg-cyan-100 text-cyan-800'
      },
      orange: {
        active: 'bg-orange-100 text-orange-800 border border-orange-200',
        badge: 'bg-orange-100 text-orange-800'
      }
    };
    const colors = colorMap[activeColor];
    return <div className="flex items-center justify-between py-3 px-4 border border-gray-200 rounded-lg bg-white" data-id="element-52">
        <span className="text-sm text-gray-600 font-medium" data-id="element-53">{label}</span>
        {editing ? <div className="flex items-center space-x-2" data-id="element-54">
            <button onClick={() => onChange(true)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${value ? colors.active : 'bg-gray-50 text-gray-400 border border-gray-200 hover:bg-gray-100'}`} data-id="element-55">
              Yes
            </button>
            <button onClick={() => onChange(false)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${!value ? 'bg-gray-200 text-gray-700 border border-gray-300' : 'bg-gray-50 text-gray-400 border border-gray-200 hover:bg-gray-100'}`} data-id="element-56">
              No
            </button>
          </div> : value ? <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${colors.badge}`} data-id="element-57">
            Yes
          </span> : <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-58">
            No
          </span>}
      </div>;
  };
  const exitTypeLabel = (exitType: ExitType): string => {
    if (!exitType) return '—';
    return exitType;
  };
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity" onClick={onClose} data-id="element-59" />

      {/* Drawer Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-id="element-60">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 bg-white" data-id="element-61">
          <div className="flex items-start justify-between mb-1" data-id="element-62">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-63">
              Experience Details
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-64">
              <XIcon className="w-5 h-5" data-id="element-65" />
            </button>
          </div>
          <div className="text-sm text-amber-600 font-medium mb-3" data-id="element-66">
            {record.Name}
          </div>

          {/* Header badges row */}
          <div className="flex items-center flex-wrap gap-2" data-id="element-67">
            <Badge variant="outline" className="text-xs px-2 py-0.5 font-normal text-gray-600 border-gray-300" data-id="element-68">
              {record.Status__c || 'Pending'}
            </Badge>
            <span className="text-sm text-gray-600" data-id="element-69">
              {guarantor.Guarantor__c}
            </span>
            {record.ABL_RT_Flag__c && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200 uppercase tracking-wider" data-id="element-70">
                ABL RT
              </span>}
            {record.GC_Flag__c && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-100 text-cyan-700 border border-cyan-200 uppercase tracking-wider" data-id="element-71">
                GC
              </span>}
            {record.Sponsor_Flag__c && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-200 uppercase tracking-wider" data-id="element-72">
                Sponsor
              </span>}
            {isEditing && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 uppercase tracking-wider" data-id="element-73">
                Editing
              </span>}
          </div>
        </div>

        <div className="h-px bg-gray-100 w-full" data-id="element-74" />

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8" data-id="element-75">
          {/* Conditions Section */}
          <section data-id="element-76">
            <div className="flex items-center justify-between mb-3" data-id="element-77">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-78">
                Conditions
              </h3>
              <button onClick={onCreateCondition} className="text-xs flex items-center text-blue-600 hover:text-blue-700 font-semibold" data-id="element-79">
                <PlusIcon className="w-3 h-3 mr-1" data-id="element-80" />
                Add Condition
              </button>
            </div>
            {conditions.length > 0 ? <div className="space-y-2" data-id="element-81">
                {conditions.map(condition => <div key={condition.id} className="bg-amber-50 border border-amber-100 rounded-lg p-3" data-id="element-82">
                    <div className="flex items-start space-x-2" data-id="element-83">
                      <AlertCircleIcon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-84" />
                      <div data-id="element-85">
                        <div className="text-sm font-bold text-gray-900" data-id="element-86">
                          {condition.type}
                        </div>
                        {condition.notes && <div className="text-xs text-gray-600 mt-1" data-id="element-87">
                            {condition.notes}
                          </div>}
                        <div className="text-[10px] text-gray-400 mt-2" data-id="element-88">
                          {new Date(condition.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 text-center" data-id="element-89">
                <p className="text-xs text-gray-500" data-id="element-90">No active conditions.</p>
              </div>}
          </section>

          <div className="h-px bg-gray-100 w-full" data-id="element-91" />

          {/* Property Information */}
          <section data-id="element-92">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-93">
              Property Information
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-4 border border-gray-100" data-id="element-94">
              {isEditing ? <div className="space-y-3" data-id="element-95">
                  <div data-id="element-96">
                    <label className="block text-xs text-gray-500 mb-1 font-medium" data-id="element-97">
                      Street Address
                    </label>
                    <input type="text" value={record.Property_Address__c.street} onChange={e => handleAddressChange('street', e.target.value)} className="w-full px-2 py-1.5 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-98" />
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-99">
                    <div data-id="element-100">
                      <label className="block text-xs text-gray-500 mb-1 font-medium" data-id="element-101">
                        City
                      </label>
                      <input type="text" value={record.Property_Address__c.city} onChange={e => handleAddressChange('city', e.target.value)} className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-102" />
                    </div>
                    <div data-id="element-103">
                      <label className="block text-xs text-gray-500 mb-1 font-medium" data-id="element-104">
                        Zip Code
                      </label>
                      <input type="text" value={record.Property_Address__c.zip} onChange={e => handleAddressChange('zip', e.target.value)} className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-105" />
                    </div>
                  </div>
                </div> : <div data-id="element-106">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-107">Address</div>
                  <div className="font-bold text-gray-900 text-sm" data-id="element-108">
                    {record.Property_Address__c.street}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-109">
                    {record.Property_Address__c.city},{' '}
                    {record.Property_Address__c.zip}
                  </div>
                </div>}

              <div className="h-px bg-gray-200 w-full" data-id="element-110" />

              {/* Property Lookup Links */}
              <div data-id="element-111">
                <div className="text-xs text-gray-500 mb-2 font-medium" data-id="element-112">
                  Property Lookup
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-113">
                  {buildPropertyLinks(record.Property_Address__c).map(link => <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-semibold transition-colors ${link.color}`} data-id="element-114">
                        <ExternalLinkIcon className={`w-3 h-3 ${link.iconColor}`} data-id="element-115" />
                        {link.name}
                      </a>)}
                </div>
              </div>

              <div className="h-px bg-gray-200 w-full" data-id="element-116" />

              {/* Type, Exit Type, Units */}
              <div className="grid grid-cols-2 gap-4" data-id="element-117">
                <div data-id="element-118">
                  <div className="text-xs text-gray-500 mb-1 font-medium" data-id="element-119">
                    Type
                  </div>
                  {isEditing ? <select value={record.Experience_Type__c} onChange={e => handleInputChange('Experience_Type__c', e.target.value as ExperienceType)} className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-120">
                      <option value="Fix & Flip" data-id="element-121">Fix & Flip</option>
                      <option value="New Construction" data-id="element-122">New Construction</option>
                      <option value="No Rehab" data-id="element-123">No Rehab</option>
                      <option value="Rental" data-id="element-124">Rental</option>
                      <option value="Purchase" data-id="element-125">Purchase</option>
                      <option value="Sale" data-id="element-126">Sale</option>
                      <option value="Rehab" data-id="element-127">Rehab</option>
                    </select> : <div className="text-sm text-gray-900" data-id="element-128">
                      {record.Experience_Type__c}
                    </div>}
                </div>
                <div data-id="element-129">
                  <div className="text-xs text-gray-500 mb-1 font-medium" data-id="element-130">
                    Units
                  </div>
                  {isEditing ? <input type="number" min="1" value={record.Units__c || 1} onChange={e => handleInputChange('Units__c', parseInt(e.target.value) || 1)} className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-131" /> : <div className="text-sm text-gray-900" data-id="element-132">
                      {record.Units__c || 1}
                    </div>}
                </div>
              </div>

              {/* Exit Type */}
              <div data-id="element-133">
                <div className="text-xs text-gray-500 mb-1 font-medium" data-id="element-134">
                  Exit Type
                </div>
                {isEditing ? <select value={record.Exit_Type__c || ''} onChange={e => handleInputChange('Exit_Type__c', e.target.value as ExitType || null)} className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-135">
                    <option value="" data-id="element-136">— None —</option>
                    <option value="Sale" data-id="element-137">Sale</option>
                    <option value="Rental Hold - Refinance" data-id="element-138">
                      Rental Hold - Refinance
                    </option>
                    <option value="Rental Hold - No Refinance" data-id="element-139">
                      Rental Hold - No Refinance
                    </option>
                    <option value="No Exit" data-id="element-140">No Exit</option>
                  </select> : <div className="text-sm text-gray-900" data-id="element-141">
                    {record.Exit_Type__c ? <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${record.Exit_Type__c === 'Sale' ? 'bg-green-100 text-green-800' : record.Exit_Type__c === 'Rental Hold - Refinance' ? 'bg-blue-100 text-blue-800' : record.Exit_Type__c === 'Rental Hold - No Refinance' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700'}`} data-id="element-142">
                        {record.Exit_Type__c}
                      </span> : <span className="text-gray-400" data-id="element-143">—</span>}
                  </div>}
              </div>
            </div>
          </section>

          {/* Financials */}
          <section data-id="element-144">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-145">
              Financials
            </h3>
            <div className="space-y-4" data-id="element-146">
              <div className="grid grid-cols-2 gap-4" data-id="element-147">
                <div data-id="element-148">
                  <div className="text-xs text-gray-500 mb-1 font-medium" data-id="element-149">
                    Purchase Price
                  </div>
                  {isEditing ? <>
                      <div className="relative" data-id="element-150">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none" data-id="element-151">
                          <span className="text-gray-400 text-sm" data-id="element-152">$</span>
                        </div>
                        <input type="number" value={record.Purchase_Price__c || ''} onChange={e => handleInputChange('Purchase_Price__c', parseFloat(e.target.value) || 0)} className="w-full pl-6 pr-2 py-1.5 text-sm font-bold text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="0" data-id="element-153" />
                      </div>
                      <div className="mt-2" data-id="element-154">
                        <label className="block text-[10px] text-gray-400 mb-0.5 uppercase tracking-wider font-bold" data-id="element-155">
                          Date
                        </label>
                        <input type="date" value={toInputDate(record.Purchase_Date__c)} onChange={e => handleInputChange('Purchase_Date__c', e.target.value)} className="w-full px-2 py-1 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500" data-id="element-156" />
                      </div>
                    </> : <>
                      <div className="font-bold text-gray-900 text-base" data-id="element-157">
                        {formatCurrency(record.Purchase_Price__c)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1" data-id="element-158">
                        {formatDate(record.Purchase_Date__c)}
                      </div>
                    </>}
                </div>
                <div data-id="element-159">
                  <div className="text-xs text-gray-500 mb-1 font-medium" data-id="element-160">
                    Sale Price
                  </div>
                  {isEditing ? <>
                      <div className="relative" data-id="element-161">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none" data-id="element-162">
                          <span className="text-gray-400 text-sm" data-id="element-163">$</span>
                        </div>
                        <input type="number" value={record.Sale_Price__c || ''} onChange={e => handleInputChange('Sale_Price__c', parseFloat(e.target.value) || 0)} className="w-full pl-6 pr-2 py-1.5 text-sm font-bold text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="0" data-id="element-164" />
                      </div>
                      <div className="mt-2" data-id="element-165">
                        <label className="block text-[10px] text-gray-400 mb-0.5 uppercase tracking-wider font-bold" data-id="element-166">
                          Date
                        </label>
                        <input type="date" value={toInputDate(record.Sale_Date__c)} onChange={e => handleInputChange('Sale_Date__c', e.target.value)} className="w-full px-2 py-1 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500" data-id="element-167" />
                      </div>
                    </> : <>
                      <div className="font-bold text-gray-900 text-base" data-id="element-168">
                        {formatCurrency(record.Sale_Price__c)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1" data-id="element-169">
                        {formatDate(record.Sale_Date__c)}
                      </div>
                    </>}
                </div>
              </div>

              <div className="h-px bg-gray-100 w-full" data-id="element-170" />

              <div className="grid grid-cols-2 gap-4" data-id="element-171">
                <div data-id="element-172">
                  {/* Renamed from "Rehab Amount" to "Budget" */}
                  <div className="text-xs text-gray-500 mb-1 font-medium" data-id="element-173">
                    Budget
                  </div>
                  {isEditing ? <div className="relative" data-id="element-174">
                      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none" data-id="element-175">
                        <span className="text-gray-400 text-sm" data-id="element-176">$</span>
                      </div>
                      <input type="number" value={record.Rehab_Amount__c || ''} onChange={e => handleInputChange('Rehab_Amount__c', parseFloat(e.target.value) || 0)} className="w-full pl-6 pr-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="0" data-id="element-177" />
                    </div> : <div className="text-sm text-gray-900" data-id="element-178">
                      {formatCurrency(record.Rehab_Amount__c)}
                    </div>}
                </div>
                <div data-id="element-179">
                  <div className="text-xs text-gray-500 mb-1 font-medium" data-id="element-180">
                    Net Profit
                  </div>
                  <div className={`font-bold text-base py-1.5 ${(record.Net_Profit__c || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-181">
                    {formatCurrency(record.Net_Profit__c)}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Flags & Indicators */}
          <section data-id="element-182">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-183">
              Flags & Indicators
            </h3>
            <div className="space-y-2" data-id="element-184">
              <FlagRow label="ABL RT Flag" value={record.ABL_RT_Flag__c} onChange={val => handleInputChange('ABL_RT_Flag__c', val)} editing={isEditing} activeColor="purple" data-id="element-185" />
              <FlagRow label="GC (General Contractor)" value={record.GC_Flag__c} onChange={val => handleInputChange('GC_Flag__c', val)} editing={isEditing} activeColor="cyan" data-id="element-186" />
              <FlagRow label="Sponsor" value={record.Sponsor_Flag__c} onChange={val => handleInputChange('Sponsor_Flag__c', val)} editing={isEditing} activeColor="orange" data-id="element-187" />
              <FlagRow label="Rehab Flag" value={record.Rehab_Flag__c} onChange={val => handleInputChange('Rehab_Flag__c', val)} editing={isEditing} activeColor="yellow" data-id="element-188" />
              <FlagRow label="Construction Flag" value={record.Construction_Flag__c} onChange={val => handleInputChange('Construction_Flag__c', val)} editing={isEditing} activeColor="yellow" data-id="element-189" />
              <FlagRow label="Verified Flag" value={record.Verified_Flag__c} onChange={val => handleInputChange('Verified_Flag__c', val)} editing={isEditing} activeColor="green" data-id="element-190" />
            </div>
          </section>

          {/* Triage Notes */}
          <section data-id="element-191">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-192">
              Triage Notes
            </h3>
            <textarea value={notes} onChange={handleNotesChange} onBlur={handleSaveNotes} placeholder="Add notes about this experience..." className="w-full h-32 p-3 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 text-sm resize-none placeholder-gray-400" data-id="element-193" />
          </section>
        </div>

        {/* Sticky Footer */}
        <div className="p-6 border-t border-gray-100 bg-white" data-id="element-194">
          <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-195">
            <button onClick={() => onVerify(record.Id)} className="flex items-center justify-center px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition-colors" data-id="element-196">
              <CheckCircleIcon className="w-5 h-5 mr-2" data-id="element-197" />
              Verify
            </button>
            <button onClick={() => onFlag(record.Id)} className="flex items-center justify-center px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold transition-colors" data-id="element-198">
              <AlertTriangleIcon className="w-5 h-5 mr-2" data-id="element-199" />
              Flag
            </button>
          </div>
          <button onClick={handleToggleEdit} className={`w-full py-3 font-semibold rounded-lg transition-colors text-sm flex items-center justify-center ${isEditing ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-200">
            {isEditing ? <>
                <SaveIcon className="w-4 h-4 mr-2" data-id="element-201" />
                Save
              </> : <>
                <PencilIcon className="w-4 h-4 mr-2" data-id="element-202" />
                Edit
              </>}
          </button>
        </div>
      </div>
    </>;
}